import { ExternalLink } from 'lucide-react'
import { Chip } from './ui/Chip'
import { cn } from '../lib/utils'
import type { Evidence } from '../api/types'

/**
 * Evidence chips for one target.
 *
 * `url` is built server-side by store._row_from_hypothesis (PubMed, doi.org,
 * UniProt), but knowledge-graph entries have an id and NO url — there is no
 * public resolver for a `KG:` accession. Those render as plain text rather than
 * as a dead link.
 *
 * A url-less chip therefore shows its `note` as visible secondary text, not only
 * as a hover title: the note is what the graph asserted ("essentiality: lethal
 * (SeedGenes)"), and behind a hover the chip reads as an opaque node id — which
 * is how a row with four KG citations looked like a row with no evidence. The
 * title stays, because the note is clipped to keep the chip inside a table cell.
 *
 * rel="noreferrer" as well as noopener: these point at external sites, and the
 * referrer would leak the internal leaderboard hostname.
 *
 * Both forms are `ui/Chip` now rather than two hand-rolled copies of its recipe —
 * this file was the recipe's precedent and had already drifted from it in radius
 * and in text size. The linked form wraps the chip in the anchor rather than
 * restyling one: the anchor is what takes the focus ring, and a `<span>` primitive
 * has no business growing an `href`.
 */
export function EvidenceLinks({ evidence }: { evidence: Evidence[] | null }) {
  // In words. An empty list means the row cites nothing, and a dash reads as
  // "there is a value here you cannot see" — the same glyph this UI uses for
  // several other things.
  if (!evidence || evidence.length === 0) {
    return <span className="text-2xs text-slate-500">None recorded</span>
  }
  return (
    <div className="flex flex-wrap gap-1.5">
      {evidence.map((e, i) => {
        const label = e.id ?? e.type
        const key = `${e.type}-${e.id ?? i}`
        if (!e.url) {
          return (
            <Chip
              key={key}
              font="mono"
              title={e.note ? `${label} — ${e.note}` : e.type}
              className="max-w-[22rem] items-baseline"
            >
              {label}
              {e.note && (
                <span className="truncate font-sans text-slate-300">{e.note}</span>
              )}
            </Chip>
          )
        }
        return (
          <a
            key={key}
            href={e.url}
            target="_blank"
            rel="noopener noreferrer"
            title={e.note ?? e.url}
            className={cn(
              'group inline-flex max-w-full rounded-md',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500',
            )}
          >
            <Chip
              font="mono"
              className="text-brand-300 group-hover:border-brand-700 group-hover:text-brand-200"
            >
              {label}
              <ExternalLink aria-hidden="true" className="h-2.5 w-2.5 shrink-0" />
            </Chip>
          </a>
        )
      })}
    </div>
  )
}
